import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  TrendingUp, LayoutDashboard, Bot, Signal, Store, 
  LogOut, Menu, CreditCard, Settings, Share2, Crown, BarChart3,
  User, ChevronDown, Shield, Plus, Bell, Wallet, Zap, Rocket,
  LineChart, Brain, Users, BookOpen, ArrowRight, Terminal, Star, HelpCircle, Ticket, Gift
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from './ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAnalystProfile, setHasAnalystProfile] = useState(false);

  useEffect(() => {
    const checkAnalystProfile = async () => {
      // Analyst profile özelliği şu an aktif değil - skip
      // if (user) {
      //   try {
      //     const token = localStorage.getItem('token');
      //     await axios.get(`${API_URL}/api/analyst/profile/me`, {
      //       headers: { Authorization: `Bearer ${token}` }
      //     });
      //     setHasAnalystProfile(true);
      //   } catch (err) {
      //     setHasAnalystProfile(false);
      //   }
      // } else {
      //   setHasAnalystProfile(false);
      // }
      setHasAnalystProfile(false);
    };
    checkAnalystProfile();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Kullanıcı Pro mu kontrol et
  const isPro = user?.is_pro || user?.membership?.plan_type === 'pro' || user?.role === 'admin';
  
  // Check if we're on a Pro page (advanced features)
  const isProPage = location.pathname.startsWith('/pro') || 
                    location.pathname.startsWith('/strategies') ||
                    location.pathname.startsWith('/marketplace') ||
                    location.pathname.startsWith('/analyst') ||
                    location.pathname.startsWith('/backtest') ||
                    location.pathname.startsWith('/dashboard') ||
                    location.pathname.startsWith('/visual-strategy-builder') ||
                    location.pathname.startsWith('/ai-copilot');

  const isCorporatePage = location.pathname.startsWith('/kurumsal');

  // Corporate page header - minimal
  if (user && isCorporatePage) {
    return (
      <nav className="sticky top-0 z-50 w-full border-b border-white/5 bg-brand-dark/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="bg-primary text-primary-foreground p-2 rounded-md">
                <TrendingUp className="h-5 w-5" />
              </div>
              <span className="text-xl font-heading font-bold">
                <span className="text-white">Borsa</span>
                <span className="text-primary">mex</span>
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" size="sm" className="border-primary/30 text-primary hover:bg-primary/10">
                <LayoutDashboard className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>
    );
  }

  // =============================================
  // PRO NAVBAR - Full featured navigation for Pro pages
  // =============================================
  if (user && isProPage) {
    return (
      <nav className="sticky top-0 z-50 w-full border-b border-white/5 bg-brand-dark/80 backdrop-blur-md">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Back to Simple Borsamex */}
              <Link 
                to="/" 
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors border border-slate-700/50 hover:border-slate-600"
              >
                <ArrowRight className="h-4 w-4 rotate-180" />
                <span>Borsamex</span>
              </Link>

              {/* Divider */}
              <div className="hidden md:block w-px h-6 bg-slate-700" />

              {/* Logo with PRO badge */}
              <Link to="/pro" className="flex items-center space-x-2 group" data-testid="navbar-logo">
                <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white p-2 rounded-md group-hover:shadow-[0_0_20px_rgba(139,92,246,0.4)] transition-all duration-300">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-heading font-bold">
                    <span className="text-white">Borsa</span>
                    <span className="text-purple-400">mex</span>
                  </span>
                  <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-purple-300 bg-purple-500/20 border border-purple-500/30 rounded uppercase">
                    Pro
                  </span>
                </div>
              </Link>
            </div>

            {/* Pro Desktop Menu */}
            <div className="hidden md:flex items-center space-x-1">
              <Link
                to="/dashboard"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/dashboard') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <LayoutDashboard className="h-4 w-4" />
                <span>Dashboard</span>
              </Link>
              
              <Link
                to="/strategies"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/strategies') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <Bot className="h-4 w-4" />
                <span>Botlar</span>
              </Link>

              <Link
                to="/pro/signals"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/pro/signals') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <Signal className="h-4 w-4" />
                <span>Sinyaller</span>
              </Link>

              <Link
                to="/marketplace"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/marketplace') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <Store className="h-4 w-4" />
                <span>Pazaryeri</span>
              </Link>

              {/* Yeni Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-primary hover:bg-primary/10 h-9 px-3">
                    <Plus className="h-4 w-4 mr-1" />
                    Yeni
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/strategies/new')}>
                    <Bot className="h-4 w-4 mr-2 text-emerald-400" />
                    Bot Oluştur
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/visual-strategy-builder')}>
                    <Zap className="h-4 w-4 mr-2 text-purple-400" />
                    Görsel Builder
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {hasAnalystProfile ? (
                    <DropdownMenuItem onClick={() => navigate('/analyst')}>
                      <Signal className="h-4 w-4 mr-2 text-blue-400" />
                      Sinyal Yayınla
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => navigate('/analyst')}>
                      <Wallet className="h-4 w-4 mr-2 text-amber-400" />
                      Sağlayıcı Ol
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Divider */}
              <div className="w-px h-6 bg-slate-700 mx-2" />

              {/* Referans Programı - Hediye İkonu */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/referans')}
                className="relative text-primary hover:text-primary hover:bg-primary/10 h-9 w-9 p-0 group"
                title="Referans Programı"
              >
                <Gift className="h-4 w-4" />
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                </span>
              </Button>

              {/* Pro'ya Geç Butonu - Sadece ücretsiz kullanıcılar için */}
              {!isPro && (
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-to-r from-primary to-emerald-500 hover:from-primary/90 hover:to-emerald-500/90 text-black font-semibold h-9 px-3"
                  data-testid="nav-upgrade-btn"
                >
                  <Crown className="h-4 w-4 mr-1.5" />
                  Pro&apos;ya Geç
                </Button>
              )}

              {/* Notifications */}
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-9 w-9 p-0">
                <Bell className="h-4 w-4" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-9 px-2">
                    <User className="h-4 w-4 mr-1" />
                    <span className="max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-slate-800">
                    <p className="font-semibold text-white">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                    {user.is_pro && (
                      <Badge className="mt-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-[10px]">PRO</Badge>
                    )}
                  </div>
                  
                  <DropdownMenuItem onClick={() => navigate('/account/subscriptions')}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Aboneliklerim
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/partner')}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Partner Programı
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Ayarlar
                  </DropdownMenuItem>
                  
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-primary">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator />
                  
                  {/* Back to Simple */}
                  <DropdownMenuItem onClick={() => navigate('/')} className="text-slate-400">
                    <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                    Basit Moda Dön
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                    <LogOut className="h-4 w-4 mr-2" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Pro Mobile Menu */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                    <Menu className="h-6 w-6" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <div className="px-3 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <User className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">{user.name}</p>
                        <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-[10px]">PRO</Badge>
                      </div>
                    </div>
                  </div>

                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="h-4 w-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/strategies')}>
                    <Bot className="h-4 w-4 mr-2" />
                    Botlarım
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/pro/signals')}>
                    <Signal className="h-4 w-4 mr-2" />
                    Sinyaller
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/marketplace')}>
                    <Store className="h-4 w-4 mr-2" />
                    Pazaryeri
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate('/strategies/new')}>
                    <Plus className="h-4 w-4 mr-2 text-primary" />
                    Bot Oluştur
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/analyst')}>
                    <Wallet className="h-4 w-4 mr-2 text-amber-400" />
                    Sinyal Sağlayıcı
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Ayarlar
                  </DropdownMenuItem>
                  
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="text-primary">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/')} className="text-slate-400">
                    <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                    Basit Moda Dön
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                    <LogOut className="h-4 w-4 mr-2" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // =============================================
  // SIMPLE NAVBAR - For landing page and basic pages
  // =============================================
  return (
    <nav className="sticky top-0 z-50 w-full border-b border-white/5 bg-brand-dark/80 backdrop-blur-md">
      <div className="container mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-testid="navbar-logo">
            <div className="bg-primary text-primary-foreground p-2 rounded-md group-hover:shadow-[0_0_20px_rgba(0,229,153,0.4)] transition-all duration-300">
              <TrendingUp className="h-5 w-5" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xl font-heading font-bold">
                <span className="text-white">Borsa</span>
                <span className="text-primary">mex</span>
              </span>
            </div>
          </Link>

          {/* Desktop Menu - Logged In (Simple Mode) */}
          {user ? (
            <div className="hidden md:flex items-center space-x-1">
              {/* Basit Nav - Sinyal Odaklı */}
              <Link
                to="/signals"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/signals') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <Signal className="h-4 w-4" />
                <span>Sinyaller</span>
              </Link>

              <Link
                to="/performans"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2
                  ${isActive('/performans') ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
              >
                <BarChart3 className="h-4 w-4" />
                <span>Performans</span>
              </Link>

              <Link
                to="/nasil-calisir"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isActive('/nasil-calisir') ? 'text-white' : 'text-slate-400 hover:text-white'}`}
              >
                Nasıl Çalışır?
              </Link>

              <Link
                to="/rehber"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5
                  ${isActive('/rehber') ? 'text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <BookOpen className="h-4 w-4" />
                Rehber
              </Link>

              {/* Divider */}
              <div className="w-px h-6 bg-slate-700 mx-2" />

              {/* Referans Programı - Hediye İkonu + Label (AMBER) */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/referans')}
                className="relative text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-9 px-2 group"
                title="Arkadaşını davet et → kredi kazan"
              >
                <Gift className="h-4 w-4 mr-1.5" />
                <span className="text-sm font-medium">Kazan</span>
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400"></span>
                </span>
              </Button>

              {/* Pro'ya Geç - Küçük Chip/Pill Stili (SIMPLE) */}
              {!isPro && (
                <Button 
                  onClick={() => navigate('/pricing')}
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary hover:bg-primary/10 h-8 px-2.5 rounded-full border border-primary/30"
                  data-testid="nav-upgrade-simple-btn"
                >
                  <Crown className="h-3.5 w-3.5 mr-1" />
                  <span className="text-sm font-medium">Pro</span>
                </Button>
              )}

              {/* Notifications */}
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-9 w-9 p-0">
                <Bell className="h-4 w-4" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-9 px-2">
                    <User className="h-4 w-4 mr-1" />
                    <span className="max-w-[100px] truncate">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-slate-800">
                    <p className="font-semibold text-white">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.email}</p>
                  </div>
                  
                  {/* Destek - Herkes görebilir ama Pro olmayanlar bilgilendirilir */}
                  <DropdownMenuItem onClick={() => navigate('/destek')} className="text-amber-400">
                    <Ticket className="h-4 w-4 mr-2" />
                    Destek
                    {!(user.is_pro || user.membership?.plan_type === 'pro') && (
                      <Crown className="h-3 w-3 ml-auto text-amber-500" />
                    )}
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate('/account/subscriptions')}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Aboneliklerim
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referans')} className="text-primary">
                    <Share2 className="h-4 w-4 mr-2" />
                    Referans Programı
                    <Badge className="ml-auto bg-primary/20 text-primary text-[10px]">YENİ</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Ayarlar
                  </DropdownMenuItem>
                  
                  {user.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-primary">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                    <LogOut className="h-4 w-4 mr-2" />
                    Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            /* Desktop Menu - Logged Out */
            <div className="hidden md:flex items-center space-x-1">
              <Link 
                to="/performans" 
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isActive('/performans') ? 'text-white' : 'text-slate-400 hover:text-white'}`}
              >
                Performans
              </Link>
              <Link 
                to="/nasil-calisir" 
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${isActive('/nasil-calisir') ? 'text-white' : 'text-slate-400 hover:text-white'}`}
              >
                Nasıl Çalışır?
              </Link>
              <Link 
                to="/rehber" 
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5
                  ${isActive('/rehber') ? 'text-white' : 'text-slate-400 hover:text-white'}`}
              >
                <BookOpen className="h-4 w-4" />
                Rehber
              </Link>
              
              {/* Divider */}
              <div className="w-px h-6 bg-slate-700 mx-3" />
              
              {/* Auth Buttons */}
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-slate-400 hover:text-white h-9">
                  Giriş Yap
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-primary hover:bg-primary/90 text-black font-semibold h-9">
                  Ücretsiz Başla
                </Button>
              </Link>
            </div>
          )}

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-1">
            {/* Hediye İkonu - Mobil (AMBER) */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(user ? '/referans' : '/referans-programi')}
              className="relative text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-9 w-9 p-0"
              title="Kazan"
            >
              <Gift className="h-5 w-5" />
              <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
              </span>
            </Button>

            {/* Pro'ya Geç İkonu - Mobil (Sadece ücretsiz kullanıcılar için) */}
            {user && !isPro && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/pricing')}
                className="bg-primary/20 hover:bg-primary/30 text-primary h-9 w-9 p-0"
                title="Pro'ya Geç - Telegram'dan anında al"
              >
                <Crown className="h-5 w-5" />
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {user ? (
                  <>
                    {/* User Info */}
                    <div className="px-3 py-3 border-b border-slate-800">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-white">{user.name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Basit Nav */}
                    <DropdownMenuItem onClick={() => navigate('/signals')}>
                      <Signal className="h-4 w-4 mr-2" />
                      Sinyaller
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/performans')}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Performans
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/nasil-calisir')}>
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Nasıl Çalışır?
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/rehber')}>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Rehber
                    </DropdownMenuItem>
                    
                    {/* Pro'ya Geç - Mobil */}
                    {!isPro && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => navigate('/pricing')}
                          className="bg-primary/10 text-primary font-semibold"
                        >
                          <Crown className="h-4 w-4 mr-2" />
                          Pro&apos;ya Geç
                          <Zap className="h-3 w-3 ml-auto" />
                        </DropdownMenuItem>
                      </>
                    )}
                    
                    {/* Referans Programı - Kazan (AMBER) */}
                    <DropdownMenuItem onClick={() => navigate('/referans')} className="text-amber-400">
                      <Gift className="h-4 w-4 mr-2" />
                      Kazan
                      <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-[10px] px-1.5">YENİ</Badge>
                    </DropdownMenuItem>
                    
                    {/* Destek - Herkes görebilir ama Pro olmayanlar bilgilendirilir */}
                    <DropdownMenuItem onClick={() => navigate('/destek')} className="text-amber-400">
                      <Ticket className="h-4 w-4 mr-2" />
                      Destek
                      {!(user.is_pro || user.membership?.plan_type === 'pro') && (
                        <Crown className="h-3 w-3 ml-auto text-amber-500" />
                      )}
                    </DropdownMenuItem>
                    
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                      <Settings className="h-4 w-4 mr-2" />
                      Ayarlar
                    </DropdownMenuItem>
                    
                    {user.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-primary">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                      <LogOut className="h-4 w-4 mr-2" />
                      Çıkış Yap
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    {/* Basit Nav */}
                    <DropdownMenuItem onClick={() => navigate('/performans')}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Performans
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/nasil-calisir')}>
                      <HelpCircle className="h-4 w-4 mr-2" />
                      Nasıl Çalışır?
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/rehber')}>
                      <BookOpen className="h-4 w-4 mr-2" />
                      Rehber
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/referans-programi')} className="text-primary">
                      <Gift className="h-4 w-4 mr-2" />
                      Referans Programı
                      <Badge className="ml-auto bg-primary/20 text-primary text-[10px] px-1.5">YENİ</Badge>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/login')}>
                      Giriş Yap
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/register')} className="text-primary font-semibold">
                      Ücretsiz Başla
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
